/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import {
  type ColumnDef,
  type Header as HeaderType,
  type RowData,
  type Table,
} from '@tanstack/react-table';

/**
 * Modified from here: https://gist.github.com/kottenator/9d936eb3e4e3c3e02598
 *
 * @param current - Current index
 * @param max - Max index
 * @returns
 */
export const userFriendlyPagination = (current: number, max: number) => {
  const last = max;
  const delta = 1;
  const left = current - delta;
  const right = current + delta + 1;
  const range = [];
  const rangeWithDots = [];
  let length: number | undefined;

  for (let pageIndex = 1; pageIndex <= last; pageIndex += 1) {
    if (
      pageIndex === 1 ||
      pageIndex === last ||
      (pageIndex >= left && pageIndex < right)
    ) {
      range.push(pageIndex);
    }
  }

  for (let rangeIndex = 0; rangeIndex < range.length; rangeIndex += 1) {
    if (length) {
      if (range[rangeIndex] - length === 2) {
        rangeWithDots.push(length + 1);
      } else if (range[rangeIndex] - length !== 1) {
        rangeWithDots.push('...');
      }
    }
    rangeWithDots.push(range[rangeIndex]);
    length = range[rangeIndex];
  }

  return rangeWithDots;
};

/**
 * Simple helper to generate inline grid style
 * to be responsive.
 *
 * Secret sauce:
 * The last element takes an "auto" width, so it will
 * fill the available space, and the result till be for 4 columns:
 * minmax(min column1Sizepx,column1Sizepx) minmax(min column2Sizepx,column2Sizepx) ... minmax(columnNSizepx, auto)
 */
export const gridGenerator = <T extends RowData>(table: Table<T>) => {
  const visibleColumns = table.getVisibleFlatColumns();
  const tableDefaultMin = table._getDefaultColumnDef().minSize;
  // 20 is the default value that is set in Tanstack. For us the min size is too
  // small so we increase it to look better per default.
  // https://github.com/TanStack/table/blob/main/docs/guide/column-sizing.md
  const defaultMin = tableDefaultMin !== 20 ? tableDefaultMin : 100;

  // If there are actions, we want the last column to be fixed sized
  // and the rest to be auto.
  return visibleColumns
    .map((column, idx) =>
      visibleColumns.length === idx + 1
        ? `minmax(${column.getSize()}px ,auto)`
        : `minmax(${
            table.getColumn(column.id)?.columnDef.minSize || defaultMin
          }px,${column.getSize()}px)`,
    )
    .join(' ');
};

/**
 * Check if the column is size constrained,
 * so we can let the column's width grow with `flex-grow: 1`
 * @param columnDef - Column definition
 */
export const isSizeConstrained = <T extends RowData>(columnDef: ColumnDef<T>) =>
  columnDef.size !== undefined && columnDef.size !== 150;

export enum ResizeDirection {
  LEFT = 'left',
  RIGHT = 'right',
}

const isLastColumnId = <T extends RowData>(
  headers: HeaderType<T, unknown>[],
  columnId: T,
) => {
  const columnIndex = headers.findIndex((header) => header.id === columnId);
  return columnIndex === headers.length - 1;
};

/**
 * Function to resize the column
 * @param direction - Direction to resize
 * @param header - The header cell that is being resized
 */
export const resizeColumn = <T extends RowData>(
  direction: ResizeDirection,
  header: HeaderType<T, unknown>,
) => {
  const RESIZE_AMOUNT = 5; // Pixels to shift
  const currentColumnId = header.column.id;
  const table = header.getContext().table;
  const maxColumnWidth = header.column.columnDef.maxSize ?? window.innerWidth;
  const minColumnWidth = header.column.columnDef.minSize ?? 0;
  const maxTableRowWidth =
    document.querySelector('.ndl-data-grid-thead')?.getBoundingClientRect()
      .width || 0;

  const adjustColumnWidth = (currentWidth: number, adjustment: number) => {
    return Math.min(
      Math.max(currentWidth + adjustment, minColumnWidth),
      maxColumnWidth,
    );
  };

  if (direction === ResizeDirection.LEFT) {
    table.setColumnSizing((currentSizes) => {
      const newWidth = adjustColumnWidth(
        currentSizes[currentColumnId],
        -RESIZE_AMOUNT,
      );
      return { ...currentSizes, [currentColumnId]: newWidth };
    });
  } else if (direction === ResizeDirection.RIGHT) {
    table.setColumnSizing((currentSizes) => {
      if (isLastColumnId(header.headerGroup.headers, currentColumnId as T)) {
        // Calculate the total width of the current columns combined
        const totalCurrentWidth = Object.values(currentSizes).reduce(
          (total, size) => total + (isNaN(size) ? 0 : size),
          0,
        );
        if (totalCurrentWidth + RESIZE_AMOUNT >= maxTableRowWidth) {
          return currentSizes;
        }
      }
      const newWidth = adjustColumnWidth(
        currentSizes[currentColumnId],
        RESIZE_AMOUNT,
      );
      return { ...currentSizes, [currentColumnId]: newWidth };
    });
  }
};

export const ACTION_CELL_SIZE = 40;

export const updateColumnWidths = <T extends RowData>(
  table: Table<T>,
  totalWidth: number,
) => {
  const columns = table.getAllColumns();
  const availableWidth = Math.floor(totalWidth);

  // Separate pinned and resizable (unpinned) columns
  const pinnedColumns = columns.filter((col) => col.getIsPinned());
  const resizableColumns = columns.filter((col) => !col.getIsPinned());

  // Sum of pinned widths (pinned columns should remain unchanged)
  const totalPinnedWidth = pinnedColumns.reduce(
    (sum, col) => sum + col.getSize(),
    0,
  );

  // Available width for resizable columns
  const availableResizableWidth = availableWidth - totalPinnedWidth;

  // Calculate min widths for resizable columns
  const resizableMinWidths = resizableColumns.map((col) =>
    col.columnDef.meta?.isActionCell
      ? ACTION_CELL_SIZE
      : Math.floor(col.columnDef.minSize ?? 0),
  );

  // Calculate max widths for resizable columns
  const resizableMaxWidths = resizableColumns.map((col) =>
    col.columnDef.meta?.isActionCell
      ? ACTION_CELL_SIZE
      : Math.floor(col.columnDef.maxSize ?? availableResizableWidth),
  );

  const sumResizableMinWidths = resizableMinWidths.reduce(
    (sum, width) => sum + width,
    0,
  );

  // If available width for resizable columns is less than or equal to the sum of their minimum widths,
  // assign each resizable column its minimum width.
  if (sumResizableMinWidths >= availableResizableWidth) {
    const newSizing = resizableColumns.reduce(
      (acc, col, index) => ({ ...acc, [col.id]: resizableMinWidths[index] }),
      {} as Record<string, number>,
    );
    // Preserve pinned columns unchanged.
    pinnedColumns.forEach((col) => {
      newSizing[col.id] = col.getSize();
    });
    table.setColumnSizing(() => newSizing);
    return;
  }

  // Calculate proportional weights for resizable columns based on columnDef.size (default 20),
  // but if `isActionCell` is true, use ACTION_CELL_SIZE.
  const resizableWeights = resizableColumns.map((col) =>
    col.columnDef.meta?.isActionCell
      ? ACTION_CELL_SIZE
      : typeof col.columnDef.size === 'number'
        ? col.columnDef.size
        : 20,
  );

  const allocatedResizableWidths = distributeProportional(
    availableResizableWidth,
    resizableWeights,
    resizableMinWidths,
    resizableMaxWidths,
  );

  // Compose the final sizing object.
  const finalSizing = {} as Record<string, number>;
  // Preserve pinned columns with current sizes.
  pinnedColumns.forEach((col) => {
    if (col.columnDef.meta?.isActionCell) {
      finalSizing[col.id] = ACTION_CELL_SIZE;
    } else {
      finalSizing[col.id] = col.getSize();
    }
  });
  // Assign computed widths to resizable columns.
  resizableColumns.forEach((col, index) => {
    finalSizing[col.id] = allocatedResizableWidths[index];
  });

  table.setColumnSizing(() => finalSizing);
};

// Helper function to distribute available width proportionally within min and max bounds.
const distributeProportional = (
  total: number,
  weights: number[],
  minBounds: number[],
  maxBounds: number[],
): number[] => {
  const count = weights.length;
  const result = new Array(count).fill(0);
  const freeIndices = new Set<number>();
  for (let index = 0; index < count; index++) {
    freeIndices.add(index);
  }
  let remaining = total;

  while (freeIndices.size > 0) {
    const free = Array.from(freeIndices);
    const totalFreeWeight = free.reduce(
      (sum, index) => sum + weights[index],
      0,
    );
    let isUpdated = false;
    for (const index of free) {
      const alloc = remaining * (weights[index] / totalFreeWeight);
      if (alloc < minBounds[index]) {
        result[index] = minBounds[index];
        remaining -= minBounds[index];
        freeIndices.delete(index);
        isUpdated = true;
      } else if (alloc > maxBounds[index]) {
        result[index] = maxBounds[index];
        remaining -= maxBounds[index];
        freeIndices.delete(index);
        isUpdated = true;
      }
    }
    if (!isUpdated) {
      for (const index of free) {
        result[index] = remaining * (weights[index] / totalFreeWeight);
      }
      break;
    }
  }

  // Round results and distribute remaining pixels to avoid rounding issues.
  const rounded = result.map((value) => Math.floor(value));
  const allocatedSum = rounded.reduce((sum, value) => sum + value, 0);
  let diff = total - allocatedSum;
  for (let index = 0; index < count && diff > 0; index++) {
    if (rounded[index] < maxBounds[index]) {
      const add = Math.min(diff, maxBounds[index] - rounded[index]);
      rounded[index] += add;
      diff -= add;
    }
  }
  return rounded;
};
